/* ============================================================
   src/shared/pricing.ts
   ============================================================
   Pure pricing functions extracted from src/App.tsx (R2a refactor).
   Both the desktop App and the mobile MobileApp import from here.

   ZERO logic changes vs the pre-extraction App.tsx — every block
   below was copied verbatim from App.tsx, with closure references
   replaced by explicit function parameters. If you change math
   here, both apps change at once. If a desktop number ever
   diverges from a mobile number, the diff is in this file.
   ============================================================ */

import { dlog } from "../debugLog";

/* ------------------------------
   TYPES
   ------------------------------ */

export type PricingItemRow = {
  id: number | string;

  category: string | null;
  category2?: string | null;
  category_id?: number | null;

  name: string;
  display_name?: string | null;

  cost: number;
  unit: string | null;
  base_unit?: string | null;

  group: string | null;
  sort_order: number | null;
  active: boolean;
  proposal_description?: string | null;
  deleted_at?: string | null;

  subcategory_id?: number | null;
  option_group_id?: number | null;
};

export type PricingCategoryRow = {
  id: number;
  name: string;
  is_active: boolean;
};

export type AddItemRow = {
  rowId: string;
  category: string;
  itemId: string;
  qty: number;
  benchType?: string;

  constructionType?: string;
  deckingId?: string;

  customName?: string;
  customQtyText?: string;
  customDescription?: string;
  customPrice?: number;
  isFixedPrice?: boolean;

  // When true, the item is priced but EXCLUDED from finalEstimate and
  // surfaced in the proposal's "Optional Add-Ons" block instead.
  optional?: boolean;
};

/* ------------------------------
   CONSTANTS
   ------------------------------ */

export const CONSTRUCTION_TYPES = [
  { value: "", label: "Construction Type", adjust: 0 },
  { value: "New_Construction", label: "New Construction", adjust: 0 },
  { value: "ReSurface", label: "Resurface", adjust: -4 },
  { value: "Second_Story", label: "Second Story", adjust: 2 },
  {
    value: "Second_Story_Resurface",
    label: "Second Story Resurface",
    adjust: 0,
  },
  { value: "Sleeper_System", label: "Sleeper System", adjust: -2 },
  {
    value: "Second_Story_Sleeper_System",
    label: "Second Story Sleeper System",
    adjust: 0,
  },
];

export const BENCH_TYPES = [
  { value: "12_flat", label: "12in Flat Bench" },
  { value: "12_back", label: "12in Flat Bench w back" },
  { value: "12_storage", label: "12in Flat Bench w storage" },
  { value: "18_flat", label: "18 inch Bench" },
  { value: "18_back", label: "18 inch Bench w back" },
  { value: "18_storage", label: "18 inch Bench w storage" },
] as const;

export type BenchTypeValue = (typeof BENCH_TYPES)[number]["value"];

/* ------------------------------
   STRING HELPERS (pure)
   ------------------------------ */

export function normalizeName(v: string) {
  return (v || "").toLowerCase().trim().replace(/\s+/g, " ");
}

export function normalizeCat(v: string) {
  return (v || "")
    .toLowerCase()
    .trim()
    .replace(/\s+/g, "_")
    .replace(/-+/g, "_");
}

/** Tolerant name key for matching pricing rows that live in different
 *  categories and are edited independently in Pricing Admin, so their
 *  names tend to drift by a single typographic character — e.g. the
 *  Decking row "IPE 5_4x6" vs a Stair row someone typed as "IPE 5_4×6"
 *  (× = U+00D7, the multiplication sign) or "IPE 5/4x6". Folding × → x
 *  and / → _ on BOTH sides of the comparison keeps stairs from silently
 *  zeroing out on a one-character mismatch. Built on normalizeName so the
 *  case/whitespace folding stays identical to every other name compare. */
export function normalizeMatchName(v: string) {
  return normalizeName(v)
    .replace(/×/g, "x") // × (U+00D7) → x
    .replace(/\//g, "_"); // 5/4 → 5_4
}

/* ------------------------------
   EXPRESSION INPUT PARSER (pure)
   ------------------------------ */

const EXPRESSION_ALLOWED = /^[0-9+\-*/().\s]+$/;

export function parseExpression(input: string): number | null {
  let raw = (input || "").trim();
  if (!raw) return null;
  if (raw.startsWith("=")) raw = raw.slice(1).trim();
  if (!raw) return null;
  if (!EXPRESSION_ALLOWED.test(raw)) return null;
  try {
    // eslint-disable-next-line no-new-func
    const result = Function(`"use strict"; return (${raw});`)();
    if (typeof result !== "number" || !Number.isFinite(result)) return null;
    return result;
  } catch {
    return null;
  }
}

/* ------------------------------
   CATEGORY HELPERS (pure)
   ------------------------------ */

export function isBenchCategory(cat: string) {
  return normalizeCat(cat || "") === "bench";
}

export function isConstructionTypeCategory(cat: string) {
  if (!cat) return false;
  const norm = normalizeCat(cat);
  return CONSTRUCTION_TYPES.some(
    (t) => t.value && normalizeCat(t.value) === norm
  );
}

export function getConstructionTypeLabel(cat: string) {
  if (!cat) return "";
  const norm = normalizeCat(cat);
  const found = CONSTRUCTION_TYPES.find(
    (t) => t.value && normalizeCat(t.value) === norm
  );
  return found?.label || cat;
}

export function getConstructionAdjustment(type: string): number {
  const found = CONSTRUCTION_TYPES.find((t) => t.value === type);
  return found?.adjust ?? 0;
}

/* ------------------------------
   PERMIT UPLIFT HELPER (pure)
   ------------------------------ */

export function getPermitTierForTotal(
  rows: PricingItemRow[],
  deckTotal: number
): { multiplier: number; threshold: number | null } {
  if (!deckTotal || !Number.isFinite(deckTotal))
    return { multiplier: 1, threshold: null };

  const permitRows = rows.filter(
    (row) =>
      (row.unit || "").toLowerCase().trim() === "global_multiplier" &&
      (row.name || "").toLowerCase().includes("permit deck")
  );

  if (!permitRows.length) return { multiplier: 1, threshold: null };

  let chosenThreshold: number | null = null;
  let chosenMultiplier = 1;

  for (const row of permitRows) {
    const match = (row.name || "").match(/less than\s+(\d+)(k?)/i);
    if (!match) continue;

    const num = Number(match[1]);
    const hasK = !!match[2];
    const threshold = hasK ? num * 1000 : num;
    const multiplier = row.cost ?? 1;

    if (deckTotal <= threshold) {
      if (chosenThreshold === null || threshold < chosenThreshold) {
        chosenThreshold = threshold;
        chosenMultiplier = multiplier;
      }
    }
  }

  return { multiplier: chosenMultiplier, threshold: chosenThreshold };
}

/* ------------------------------
   SMALL JOB UPLIFT HELPER (pure)
   ------------------------------ */

export function getSmallJobTierForTotal(
  rows: PricingItemRow[],
  deckTotal: number
): { multiplier: number; threshold: number | null } {
  if (!deckTotal || !Number.isFinite(deckTotal))
    return { multiplier: 1, threshold: null };

  const smallRows = rows.filter((row) => {
    const unit = (row.unit || "").toLowerCase().trim();
    const name = (row.name || "").toLowerCase();
    return unit === "global_multiplier" && name.includes("deck less than");
  });

  if (!smallRows.length) return { multiplier: 1, threshold: null };

  const parsed = smallRows
    .map((row) => {
      const match = (row.name || "").match(/less than\s+(\d+)(k?)/i);
      if (!match) return null;

      const num = Number(match[1]);
      const hasK = !!match[2];
      const threshold = hasK ? num * 1000 : num;

      if (!Number.isFinite(threshold) || threshold > 9000) return null;

      return { threshold, multiplier: row.cost ?? 1 };
    })
    .filter(Boolean) as { threshold: number; multiplier: number }[];

  if (parsed.length === 0) return { multiplier: 1, threshold: null };

  const candidates = parsed
    .filter((t) => deckTotal <= t.threshold)
    .sort((a, b) => a.threshold - b.threshold);

  if (candidates.length === 0) return { multiplier: 1, threshold: null };

  return {
    multiplier: candidates[0].multiplier,
    threshold: candidates[0].threshold,
  };
}

/* ------------------------------
   STAIRS — shared pricing helper
   ------------------------------ */

export function computeEffectiveStairsRate(params: {
  pricingItems: PricingItemRow[];
  selectedDecking: PricingItemRow | undefined;
  stairOptionRow: PricingItemRow | null;
}): { baseUnit: number; effectiveRate: number; tooltip: string } {
  const { pricingItems, selectedDecking, stairOptionRow } = params;

  if (!selectedDecking) {
    return {
      baseUnit: 0,
      effectiveRate: 0,
      tooltip: "Select Decking Type to price stairs",
    };
  }

  const deckNm = normalizeMatchName(selectedDecking.name || "");
  const baseStairRow = pricingItems.find((it) => {
    if (it.active === false) return false;
    if ((it.deleted_at ?? null) != null) return false;

    const cat = normalizeCat(it.category || "");
    if (cat !== "stair") return false;

    return normalizeMatchName(it.name || "") === deckNm;
  });

  const baseUnit = Number(baseStairRow?.cost || 0);
  if (baseUnit <= 0) {
    // Genuinely missing base stair price (not just a name-drift, which the
    // tolerant match above already absorbs). Surface it loudly: a silent $0
    // stair line is how this class of bug reaches the field unnoticed.
    const tooltip = `⚠ Missing base stair price — add a "Stair" pricing item named "${selectedDecking.name}" in Pricing Admin (stairs are priced off the decking-name match).`;
    // eslint-disable-next-line no-console
    console.warn(
      `[stairs] No active "stair" pricing item matches decking "${selectedDecking.name}" (matched as "${deckNm}"). Stairs will price at $0 until a matching Stair item exists.`
    );
    return {
      baseUnit: 0,
      effectiveRate: 0,
      tooltip,
    };
  }

  if (!stairOptionRow) {
    return {
      baseUnit,
      effectiveRate: baseUnit,
      tooltip: `Base stairs: $${baseUnit.toFixed(2)}/lf`,
    };
  }

  const optUnit = String(stairOptionRow.unit || "")
    .toLowerCase()
    .trim();
  const optCost = Number(stairOptionRow.cost || 0);
  const optName = (stairOptionRow.name || "Stair Option").toString().trim();

  let effectiveRate = baseUnit;

  if (optUnit === "multiplier") {
    effectiveRate = baseUnit * optCost;
    return {
      baseUnit,
      effectiveRate,
      tooltip: `${optName}: ${optCost} × $${baseUnit.toFixed(
        2
      )}/lf = $${effectiveRate.toFixed(2)}/lf`,
    };
  }

  if (optUnit === "addon_lf") {
    effectiveRate = baseUnit + optCost;
    return {
      baseUnit,
      effectiveRate,
      tooltip: `${optName}: $${baseUnit.toFixed(2)}/lf + $${optCost.toFixed(
        2
      )}/lf = $${effectiveRate.toFixed(2)}/lf`,
    };
  }

  if (optUnit === "lf") {
    effectiveRate = optCost;
    return {
      baseUnit,
      effectiveRate,
      tooltip: `${optName}: $${effectiveRate.toFixed(2)}/lf (override)`,
    };
  }

  effectiveRate = optCost;
  return {
    baseUnit,
    effectiveRate,
    tooltip: `${optName}: $${effectiveRate.toFixed(2)}/lf`,
  };
}

/* ------------------------------
   SKIRTING — effective rate helper
   ------------------------------ */

export function computeEffectiveSkirtingRate(params: {
  selectedDeckingUnit: number;
  skirtingRow: PricingItemRow | undefined;
}): { effectiveRate: number; tooltip: string } {
  const { selectedDeckingUnit, skirtingRow } = params;

  if (!skirtingRow) {
    return { effectiveRate: 0, tooltip: "Select skirting/lattice type" };
  }

  const unit = String(skirtingRow.unit || "").toLowerCase().trim();
  const cost = Number(skirtingRow.cost || 0);

  if (unit === "multiplier") {
    const rate = selectedDeckingUnit * cost;
    return {
      effectiveRate: rate,
      tooltip: `${skirtingRow.name}: ${cost} × $${selectedDeckingUnit.toFixed(
        2
      )}/sf = $${rate.toFixed(2)}/sf`,
    };
  }

  if (unit === "addon_sf") {
    const rate = selectedDeckingUnit + cost;
    return {
      effectiveRate: rate,
      tooltip: `${skirtingRow.name}: $${selectedDeckingUnit.toFixed(
        2
      )}/sf + $${cost.toFixed(2)}/sf = $${rate.toFixed(2)}/sf`,
    };
  }

  return {
    effectiveRate: cost,
    tooltip: `${skirtingRow.name}: $${cost.toFixed(2)}/sf`,
  };
}

/* ------------------------------
   BENCH ADDON PER-LF (pure)

   Bench back/storage upcharges live in Supabase as $/lf "addon_lf"
   rows under category "Bench" (both 12" and 18" rows share the same
   category in production). Size is encoded in the name ("12in..." vs
   "18 inch..."); kind in the trailing suffix ("w back" / "w storage").
   The matcher tolerates a known "storge" typo on the 12" storage row.

   Returns the per-LF dollar addon (not a percentage). Callers add it
   to the base bench rate: unitCost = baseBenchLf + addonPerLf.
   ------------------------------ */
const BENCH_ADDON_FALLBACK_PER_LF = { back: 40, storage: 10 } as const;

export function findBenchAddonPerLf(
  pricingItems: PricingItemRow[],
  size: "12" | "18",
  kind: "back" | "storage"
): number {
  const isSize = (name: string) => {
    const nm = name.toLowerCase();
    return size === "12"
      ? /\b12\s*in(?:ch)?\b/.test(nm)
      : /\b18\s*in(?:ch)?\b/.test(nm);
  };
  const isKind = (name: string) => {
    const nm = name.toLowerCase();
    return kind === "back"
      ? /\bw\s+back\b/.test(nm)
      : /\bw\s+stor(?:age|ge)\b/.test(nm);
  };

  const row = pricingItems.find((it) => {
    if (it.active === false) return false;
    if ((it.deleted_at ?? null) != null) return false;
    if (!normalizeCat(it.category || "").startsWith("bench")) return false;
    const nm = String(it.name || "");
    return isSize(nm) && isKind(nm);
  });

  const cost = Number(row?.cost ?? NaN);
  if (!Number.isFinite(cost) || cost <= 0) {
    return BENCH_ADDON_FALLBACK_PER_LF[kind];
  }
  return cost;
}

/* ------------------------------
   OPTION FILTERS (pure)

   Each returns an active, non-deleted, sorted PricingItemRow[] for
   one category. Sort order: sort_order then name.
   ------------------------------ */

const byOrderThenName = (a: PricingItemRow, b: PricingItemRow) => {
  const aOrder = a.sort_order ?? 999;
  const bOrder = b.sort_order ?? 999;
  if (aOrder !== bOrder) return aOrder - bOrder;
  return (a.name ?? "").localeCompare(b.name ?? "");
};

export function filterDeckingOptions(pricingItems: PricingItemRow[]) {
  return pricingItems
    .filter((item) => {
      const cat = (item.category || "").toLowerCase().trim();
      const unit = (item.unit || "").toLowerCase().trim();
      const isDeckMaterial =
        (cat === "decking" || cat === "ipe" || cat === "composite_decking") &&
        unit === "sf";
      return (
        isDeckMaterial &&
        item.active !== false &&
        (item.deleted_at ?? null) == null
      );
    })
    .sort(byOrderThenName);
}

export function filterStairsOptions(pricingItems: PricingItemRow[]) {
  return pricingItems
    .filter((item) => {
      const cat1 = (item.category || "").toLowerCase().trim();
      const cat2 = (item.category2 || "").toLowerCase().trim();
      return (
        (cat1 === "stair_options" || cat2 === "stair_options") &&
        item.active !== false &&
        (item.deleted_at ?? null) == null
      );
    })
    .sort(byOrderThenName);
}

/**
 * Stair_options rows that are add-ons, not a main stairs TYPE. They stay fully
 * available under the "Stair options" +Add Item category (which resolves through
 * addItemOptionsForRow, not this list) and keep their stair_options pricing.
 *
 * Matched by id, not name: pricing_items2 is global (no org_id), so ids are
 * stable and a rename in Pricing Admin can't silently un-hide a row.
 */
export const MAIN_STAIRS_HIDDEN_IDS = new Set<string>([
  "cdc7da7a-0284-48da-b10c-4b6b884ff971", // Platform stairs(sf)
  "9b5c93d8-078a-4643-a1ff-49b26e64603f", // Mitered Treads
  "6bffdbdf-406f-470e-889e-d6646cca7fa2", // Curved Step (lf)
]);

/**
 * Options for the MAIN Stairs dropdown. Takes the rows filterStairsOptions()
 * already produced, so it inherits a frozen file's catalog rather than the live
 * one. Presentation-only: computeEstimate still prices against the unfiltered
 * list, so an estimate that selected a hidden row keeps its exact subtotal and
 * its proposal label.
 */
export function excludeHiddenMainStairs(stairOptionRows: PricingItemRow[]) {
  return stairOptionRows.filter(
    (item) => !MAIN_STAIRS_HIDDEN_IDS.has(String(item.id))
  );
}

export function filterFastenerOptions(pricingItems: PricingItemRow[]) {
  return pricingItems
    .filter((item) => {
      const cat = item.category?.toLowerCase().trim();
      return (
        cat &&
        cat.includes("fasten") &&
        item.active !== false &&
        (item.deleted_at ?? null) == null
      );
    })
    .sort(byOrderThenName);
}

export function filterDemoOptions(pricingItems: PricingItemRow[]) {
  return pricingItems
    .filter((item) => {
      const cat = (item.category || "").toLowerCase().trim();
      return (
        (cat === "demolition" || cat === "demo") &&
        item.active !== false &&
        (item.deleted_at ?? null) == null
      );
    })
    .sort(byOrderThenName);
}

export function filterSkirtingOnlyOptions(pricingItems: PricingItemRow[]) {
  return pricingItems
    .filter((item) => {
      const cat = (item.category || "").toLowerCase().trim();
      const cat2 = (item.category2 || "").toLowerCase().trim();
      return (
        (cat === "skirting" || cat2 === "skirting") &&
        item.active !== false &&
        (item.deleted_at ?? null) == null
      );
    })
    .sort(byOrderThenName);
}

export function filterLatticeOnlyOptions(pricingItems: PricingItemRow[]) {
  return pricingItems.filter((item) => {
    const cat = (item.category || "").toLowerCase().trim();
    const cat2 = (item.category2 || "").toLowerCase().trim();
    return (
      (cat === "lattice" || cat2 === "lattice") &&
      item.active !== false &&
      (item.deleted_at ?? null) == null
    );
  });
}

export function filterSkirtingLatticeOptions(
  pricingItems: PricingItemRow[],
  skirtingCategory: string
) {
  if (skirtingCategory === "Skirting") {
    return filterSkirtingOnlyOptions(pricingItems);
  }
  if (skirtingCategory === "Lattice") {
    return filterLatticeOnlyOptions(pricingItems);
  }
  return [] as PricingItemRow[];
}

export function filterRailingOptions(pricingItems: PricingItemRow[]) {
  return pricingItems
    .filter((item) => {
      const cat = (item.category ?? "").toLowerCase().trim();
      const unit = (item.unit ?? "").toLowerCase().trim();
      return (
        cat === "railing" &&
        unit === "lf" &&
        item.active !== false &&
        (item.deleted_at ?? null) == null
      );
    })
    .sort(byOrderThenName);
}

/* Unit a NEW Pricing Admin item starts with. Railing always starts as "lf" —
   filterRailingOptions above drops anything else from the Railing Type
   dropdown — as does any category whose existing active items are all "lf".
   Everything else keeps the old "ea". The Unit box stays editable. */
export function defaultUnitForNewItem(
  categoryName: string | null | undefined,
  categoryItems: Pick<PricingItemRow, "unit" | "active" | "deleted_at">[]
): string {
  if (normalizeCat(categoryName || "") === "railing") return "lf";
  const live = categoryItems.filter(
    (it) => it.active !== false && (it.deleted_at ?? null) == null
  );
  const allLf =
    live.length > 0 &&
    live.every((it) => (it.unit ?? "").toLowerCase().trim() === "lf");
  return allLf ? "lf" : "ea";
}

export function filterConstructionOptions(pricingItems: PricingItemRow[]) {
  return pricingItems
    .filter(
      (item) =>
        (item.category || "").toLowerCase().trim() === "construction_options"
    )
    .sort((a, b) => (a.sort_order ?? 999) - (b.sort_order ?? 999));
}

/* ------------------------------
   ADD-ITEM CATEGORY LIST (pure)
   ------------------------------ */

/* ------------------------------
   CATEGORY NAMES THE PRICING CODE DEPENDS ON (pure)

   Items are matched to the Estimator by their category TEXT, and several
   rules key off that text: exact names (Railing, Stair_options, Misc,
   Demolition/Demo, the Add Item BLOCKED list, construction types) and parts
   of names ("deck" / "rail" → decking- and railing-linked repricing, "fasten"
   → Fasteners dropdown, "bench…" → bench lookup, "skirt"/"latt" → Visual
   Library). Pricing Admin uses this to lock those categories (no rename or
   hide) and to refuse a new or renamed category that would trip one.
   Returns why the name matters, or null when it is free to use.
   ------------------------------ */

const PRICING_CATEGORY_NAMES = new Set(
  [
    "bench",
    "misc",
    "stair",
    "stairs",
    "stair_options",
    "railing",
    "demolition",
    "demo",
    "decking",
    "decking_options",
    "composite_decking",
    "ipe",
    "construction",
    "construction_options",
    "construction_types",
    "skirting",
    "lattice",
    "fasteners",
    "price_adjusters",
    "global_multiplier",
    "uplift",
    "uplifts",
    "admin",
    "internal",
  ].map(normalizeCat)
);

const PRICING_CATEGORY_WORDS = ["deck", "rail", "fasten", "skirt", "latt"];

export function categoryNameUsedByPricing(name: string): string | null {
  const n = normalizeCat(name);
  if (!n) return null;
  if (PRICING_CATEGORY_NAMES.has(n) || isConstructionTypeCategory(n)) {
    return "The pricing math uses this category by name.";
  }
  if (n.startsWith("bench")) {
    return "Category names starting with “bench” are read by the bench pricing.";
  }
  const word = PRICING_CATEGORY_WORDS.find((w) => n.includes(w));
  if (word) {
    return `Category names containing “${word}” change how the Estimator prices items.`;
  }
  return null;
}

export function buildAddItemCategories(
  pricingCategories: PricingCategoryRow[]
): string[] {
  // Compared through normalizeCat on BOTH sides, so a category is blocked no
  // matter which spelling Pricing Admin stored it under ("Construction
  // Options", "construction options", "construction_options" all fold to the
  // same key). A plain toLowerCase() missed the stored underscore keys, which
  // is how "Construction Options" — a main-section-only category — was still
  // reaching the Add Item dropdown.
  const BLOCKED = new Set(
    [
      "uplift",
      "uplifts",
      "price_adjusters",
      "global_multiplier",
      "admin",
      "internal",
      "construction",
      "construction_options",
      "construction_types",
      "decking",
      "composite_decking",
      "ipe",
      "stair",
      "stairs",
    ].map(normalizeCat)
  );

  const cats = (pricingCategories || [])
    .filter((c) => c.is_active)
    .map((c) => (c?.name || "").trim())
    .filter(Boolean)
    .filter((name) => !BLOCKED.has(normalizeCat(name)));

  const hasMisc = cats.some((c) => normalizeCat(c) === "misc");
  return hasMisc ? cats : ["Misc", ...cats];
}

/* ------------------------------
   ADD-ITEM OPTIONS FOR ONE ROW (pure)
   ------------------------------ */

export function addItemOptionsForRow(
  pricingItems: PricingItemRow[],
  row: AddItemRow
): PricingItemRow[] {
  const want = normalizeCat(row.category || "");
  if (!want) return [];
  if (want === "misc") return [];
  if (want === "construction_options") return [];

  const wantAliases = new Set<string>([want]);
  if (want === "demolition") wantAliases.add("demo");

  return pricingItems
    .filter((it) => {
      if (it.active === false) return false;
      if ((it.deleted_at ?? null) != null) return false;

      const cat1 = normalizeCat(it.category || "");
      const cat2 = normalizeCat((it as any).category2 || "");

      return wantAliases.has(cat1) || wantAliases.has(cat2);
    })
    .sort(byOrderThenName);
}

/* ------------------------------
   FROZEN-WINS CATALOG MERGE (pure)

   On a frozen estimate, prices must come from the file's frozen snapshot, but
   genuinely-new catalog items (added to Pricing Admin after the file was
   saved) must still be priceable instead of resolving to $0. This merge starts
   from the frozen array — so every frozen id keeps its frozen row, found first
   by `.find` downstream — and APPENDS only live rows whose id is absent from
   the snapshot. A frozen id is never replaced, so its price can never drift to
   live. A genuinely-new id resolves against the appended live row and prices
   at the live price.

   global_multiplier rows are excluded from the appended set: they feed the
   permit / small-deck AGGREGATE scans (getPermitTierForTotal /
   getSmallJobTierForTotal, which filter the whole catalog), so appending a new
   one could move a frozen estimate's uplift math. They are never user-addable
   line items anyway, so excluding them never blocks pricing a real new item.

   Returns the frozen array unchanged (same reference) when there is nothing new
   to append, keeping memoization stable and the result provably identical to
   the pre-decoupling behavior.
   ------------------------------ */

export function mergeFrozenWithLive(
  frozenItems: PricingItemRow[],
  liveItems: PricingItemRow[] | null | undefined
): PricingItemRow[] {
  if (!liveItems || !liveItems.length) return frozenItems;
  const frozenIds = new Set(frozenItems.map((i) => String(i.id)));
  const newLiveItems = liveItems.filter((i) => {
    if (frozenIds.has(String(i.id))) return false;
    const unit = (i.unit || "").toLowerCase().trim();
    if (unit === "global_multiplier") return false;
    return true;
  });
  return newLiveItems.length ? [...frozenItems, ...newLiveItems] : frozenItems;
}

/* ------------------------------
   BENCH BASE-ROW LOOKUP (pure)

   Bench add-items look up their base $/lf by matching:
     name == "<decking> bench"        (for 12in)
     name == "<decking> bench 18in"   (for 18in)

   Strict pass: also requires category to normalize to "bench" or
   "bench_18in". Fallback pass: tolerates a corrupted category text
   (one row in production has category="Bench12in Flat Bench"
   instead of "Bench" — a 2026-05-16 migration artifact). The name
   "<deck> bench" / "<deck> bench 18in" is the authoritative signal;
   the fallback just relaxes the category check to "starts with
   bench" so a mangled label can't hide an otherwise-valid row.
   ------------------------------ */
export function findBenchBaseRow(
  pricingItems: PricingItemRow[],
  deckName: string,
  benchType: string
): PricingItemRow | undefined {
  const trimmedDeck = (deckName || "").trim();
  if (!trimmedDeck) return undefined;

  const is18 = String(benchType || "").startsWith("18_");
  const baseNeedle = is18
    ? normalizeName(`${trimmedDeck} bench 18in`)
    : normalizeName(`${trimmedDeck} bench`);

  const isRowActive = (it: PricingItemRow) =>
    it.active !== false && (it.deleted_at ?? null) == null;

  const strict = pricingItems.find((it) => {
    if (!isRowActive(it)) return false;
    const cat = normalizeCat(it.category || "");
    const wantCat = is18 ? "bench_18in" : "bench";
    if (cat !== wantCat) return false;
    return normalizeName(it.name || "") === baseNeedle;
  });
  if (strict) return strict;

  return pricingItems.find((it) => {
    if (!isRowActive(it)) return false;
    const cat = normalizeCat(it.category || "");
    if (!cat.startsWith("bench")) return false;
    if (normalizeName(it.name || "") !== baseNeedle) return false;
    const nameHas18 =
      / bench 18in\b/.test(" " + normalizeName(it.name || ""));
    return is18 === nameHas18;
  });
}

/* ============================================================
   ADD-ITEM ROW PRICING (pure)

   This is the giant per-row pricer that App.tsx used to do
   inline. Lifted here verbatim. Returns an enriched row with
   unitCost, lineBase, unit/unitLabel, tooltip, isFixedPrice.
   ============================================================ */

export type DetailedAddItem = AddItemRow & {
  picked: any;
  unitCost: number;
  lineBase: number;
  unitLabel: string;
  unit: string;
  tooltip: string;
  isFixedPrice: boolean;
  displayName: string;
};

export function priceAddItem(
  row: AddItemRow,
  ctx: {
    pricingItems: PricingItemRow[];
    selectedDecking: PricingItemRow | undefined;
    selectedRailing: PricingItemRow | undefined;
    selectedStairsRow: PricingItemRow | undefined;
  }
): DetailedAddItem {
  const { pricingItems, selectedDecking, selectedRailing, selectedStairsRow } =
    ctx;

  const rowCat = normalizeCat(row.category || "");
  if ((row.category || "").toLowerCase().includes("stair")) {
    dlog("ADD-ITEM DEBUG:", {
      category: row.category,
      rowCat,
      qty: row.qty,
      itemId: row.itemId,
    });
  }

  const isMisc = rowCat === "misc";
  let pickedRow = pricingItems.find(
    (p) => String(p.id) === String(row.itemId)
  );

  const opts = addItemOptionsForRow(pricingItems, row);
  const pickedOpt = opts.find(
    (o) => String((o as any).id) === String(row.itemId)
  );

  // Bench: match pricing item by benchType label if itemId is blank
  if (!pickedRow && rowCat === "bench") {
    const bt = (row as any)?.benchType || "";
    const benchLabel =
      BENCH_TYPES.find((x) => x.value === bt)?.label || "";
    const benchLabelLc = benchLabel.toLowerCase();
    const deckName = (selectedDecking?.name || "").toString().trim();
    const deckNameLc = deckName.toLowerCase();

    const wants12 = bt.includes("12");
    const wants18 = bt.includes("18");
    const wantsBack = bt.includes("back");
    const wantsStorage = bt.includes("storage");

    const exactBenchName = benchLabel.trim();

    if (deckNameLc) {
      pickedRow = pricingItems.find((p) => {
        const cat = normalizeCat(p.category || "");
        if (cat !== "bench") return false;
        const nameLc = String(p.name || "").toLowerCase();
        if (!nameLc.includes(deckNameLc)) return false;

        if (
          benchLabelLc &&
          (nameLc.includes(benchLabelLc) || benchLabelLc.includes(nameLc))
        ) {
          return true;
        }
        if (wants12 && !nameLc.includes("12")) return false;
        if (wants18 && !nameLc.includes("18")) return false;
        if (wantsBack && !nameLc.includes("back")) return false;
        if (wantsStorage && !nameLc.includes("storage")) return false;
        return nameLc.includes("bench");
      }) as any;
    }

    if (!pickedRow && exactBenchName) {
      pickedRow = pricingItems.find((p) => {
        const cat = normalizeCat(p.category || "");
        if (cat !== "bench") return false;
        const name = String(p.name || "").trim();
        return name === exactBenchName;
      }) as any;
    }

    if (!pickedRow) {
      pickedRow = pricingItems.find((p) => {
        const cat = normalizeCat(p.category || "");
        if (cat !== "bench") return false;
        const nameLc = String(p.name || "").toLowerCase();

        if (
          benchLabelLc &&
          (nameLc.includes(benchLabelLc) || benchLabelLc.includes(nameLc))
        ) {
          return true;
        }
        if (wants12 && !nameLc.includes("12")) return false;
        if (wants18 && !nameLc.includes("18")) return false;
        if (wantsBack && !nameLc.includes("back")) return false;
        if (wantsStorage && !nameLc.includes("storage")) return false;

        return nameLc.includes("bench");
      }) as any;
    }
  }

  const picked: any = pickedRow || pickedOpt || null;

  const pickedNameRaw = (pickedRow?.name ?? pickedOpt?.name ?? "").toString();
  const pickedName = pickedNameRaw;
  const pickedNameLc = pickedNameRaw.toLowerCase();

  let lineBase = 0;
  let tooltip = "";
  let unitLabel = ((picked as any)?.unit || "ea").toString();
  let unit = unitLabel;
  let displayUnitCost = 0;

  const isAddItemStairOptions = rowCat === "stair_options";

  if (isAddItemStairOptions) {
    const qtySafe = Number(row.qty || 0);

    const baseStairs = Number(selectedStairsRow?.cost || 0);
    const baseStairsName = (selectedStairsRow?.name || "").toString().trim();

    if (!baseStairsName || baseStairs <= 0) {
      displayUnitCost = 0;
      lineBase = 0;
      tooltip = "Select Stair Options (top of estimator) to price this item";
    } else {
      displayUnitCost = baseStairs;
      lineBase = displayUnitCost * qtySafe;

      unitLabel = "lf";
      unit = "lf";
    }
  }

  // 1) MISC = fixed price, no uplift
  if (isMisc) {
    const priceSafe = Number(row.customPrice || 0);
    const displayName = (row.customName || "").trim() || "Misc Item";
    const lineBaseLocal = priceSafe * 1;

    return {
      ...row,
      qty: 1,
      picked: { name: displayName } as any,
      unitCost: priceSafe,
      lineBase: lineBaseLocal,
      unitLabel: "ea",
      unit: "ea",
      tooltip: `Fixed price: $${priceSafe.toFixed(2)} (no uplift)`,
      isFixedPrice: true,
      displayName,
    };
  }

  // 2) CONSTRUCTION TYPE ROW
  const isConstructionRow = isConstructionTypeCategory(row.category || "");
  if (isConstructionRow) {
    const qtySafe = Number(row.qty || 0);

    const deckingRowForThisLine = pricingItems.find(
      (d) => String(d.id) === String(row.deckingId || "")
    );

    const baseRateSf = deckingRowForThisLine?.cost ?? 0;
    const ctLabel = getConstructionTypeLabel(row.category || "") || "";
    const dbAdjRow = pricingItems.find((it) => {
      if ((it.unit || "").toLowerCase().trim() !== "decking_adjustment")
        return false;
      const cn = normalizeName(it.name || "");
      const ct = normalizeName(ctLabel || "");
      return (
        cn === ct ||
        (ct && cn.includes(ct)) ||
        (cn && ct.includes(cn)) ||
        cn.startsWith(ct) ||
        ct.startsWith(cn)
      );
    });
    const adjSf = dbAdjRow
      ? Number(dbAdjRow.cost || 0)
      : getConstructionAdjustment(row.category || "");
    const rateSf = baseRateSf + adjSf;

    const lineBaseLocal =
      !deckingRowForThisLine || !row.deckingId || rateSf <= 0
        ? 0
        : rateSf * qtySafe;

    return {
      ...row,
      qty: qtySafe,
      picked: {
        name: `${ctLabel} – ${deckingRowForThisLine?.name || ""}`,
      } as any,
      unitCost: rateSf,
      lineBase: lineBaseLocal,
      unitLabel: "sf",
      unit: "sf",
      tooltip: !row.deckingId
        ? "Select Decking Type"
        : `$${rateSf.toFixed(2)} / sf  ($${baseRateSf.toFixed(2)}${
            adjSf >= 0 ? " + " : " - "
          }${Math.abs(adjSf).toFixed(2)})`,
      isFixedPrice: false,
      displayName: `${ctLabel} – ${deckingRowForThisLine?.name || ""}`,
    };
  }

  // 3A) BENCH PRICING
  if (normalizeCat(row.category || "") === "bench") {
    const qtyLf = Number(row.qty || 0);
    const bt = String(row.benchType || "12_flat");

    const deckName = (selectedDecking?.name || "").trim();
    const prettyType =
      BENCH_TYPES.find((x) => x.value === bt)?.label || "Bench";

    if (!deckName) {
      return {
        ...row,
        qty: qtyLf,
        picked: { name: prettyType } as any,
        unitCost: 0,
        lineBase: 0,
        unitLabel: "lf",
        unit: "lf",
        tooltip: "Select Decking Type to price bench",
        isFixedPrice: false,
        displayName: prettyType,
      };
    }

    const is18 = bt.startsWith("18_");

    const baseNeedle = is18
      ? normalizeName(`${deckName} bench 18in`)
      : normalizeName(`${deckName} bench`);

    const baseRow = findBenchBaseRow(pricingItems, deckName, bt);

    const baseBenchLf = Number(baseRow?.cost || 0);

    if (baseBenchLf <= 0) {
      return {
        ...row,
        qty: qtyLf,
        picked: { name: prettyType } as any,
        unitCost: 0,
        lineBase: 0,
        unitLabel: "lf",
        unit: "lf",
        tooltip: is18
          ? `Missing Supabase price: "${deckName} bench 18in" (Category: Bench_18in)`
          : `Missing Supabase price: "${deckName} bench" (Category: Bench)`,
        isFixedPrice: false,
        displayName: prettyType,
      };
    }

    const pickedBench = baseRow
      ? ({
          ...baseRow,
          name: prettyType,
          proposal_description:
            (baseRow as any)?.proposal_description ||
            (baseRow as any)?.description ||
            (baseRow as any)?.details ||
            "",
        } as any)
      : ({ name: prettyType } as any);

    const size: "12" | "18" = is18 ? "18" : "12";
    const addBack = bt.includes("_back")
      ? findBenchAddonPerLf(pricingItems, size, "back")
      : 0;
    const addStorage = bt.includes("_storage")
      ? findBenchAddonPerLf(pricingItems, size, "storage")
      : 0;
    const addonPerLf = addBack + addStorage;

    const unitCost = baseBenchLf + addonPerLf;
    const tooltipBench =
      addonPerLf > 0
        ? `${prettyType}: $${unitCost.toFixed(
            2
          )}/lf = base $${baseBenchLf.toFixed(
            2
          )} + add $${addonPerLf.toFixed(2)}/lf`
        : `${prettyType}: $${unitCost.toFixed(
            2
          )}/lf = base $${baseBenchLf.toFixed(2)}`;

    const lineBaseLocal = unitCost * qtyLf;

    return {
      ...row,
      qty: qtyLf,
      picked: pickedBench as any,
      unitCost,
      lineBase: lineBaseLocal,
      unitLabel: "lf",
      unit: "lf",
      tooltip: tooltipBench,
      isFixedPrice: false,
      displayName: prettyType,
    };
  }

  // STAIR OPTIONS (Add Item) — same pricing as MAIN stairs
  if (normalizeCat(row.category || "") === "stair_options") {
    const qtyLf = Number(row.qty || 0);

    const stairOptionRow = pricingItems.find(
      (p) => String(p.id) === String(row.itemId)
    ) as PricingItemRow | undefined;

    const stairsCalc = computeEffectiveStairsRate({
      pricingItems,
      selectedDecking,
      stairOptionRow: stairOptionRow || null,
    });

    const unitCost = stairsCalc.effectiveRate;
    const lineBaseLocal = unitCost * qtyLf;

    return {
      ...row,
      qty: qtyLf,
      picked: {
        name: (stairOptionRow?.name || "Stair Option").toString(),
      } as any,
      unitCost,
      lineBase: lineBaseLocal,
      unitLabel: "lf",
      unit: "lf",
      tooltip: stairsCalc.tooltip,
      isFixedPrice: false,
      displayName: (stairOptionRow?.name || "Stair Option").toString(),
    };
  }

  // 3) If no type picked yet, return a safe row
  if (!picked) {
    const qtySafe = Number(row.qty || 0);
    return {
      ...row,
      qty: qtySafe,
      picked: null,
      unitCost: 0,
      lineBase: 0,
      unitLabel: "",
      unit: "",
      tooltip: row.category ? "Select Type" : "Select Category",
      isFixedPrice: false,
      displayName: "",
    };
  }

  // 4) Default pricing setup
  const qty = Number(row.qty || 0);
  const pickedUnit = ((picked as any).unit || "").toLowerCase().trim();

  const isPlanter = pickedName.includes("planter");
  const isRamp = pickedName.includes("ramp");

  const baseDeckSf = selectedDecking?.cost ?? 0;
  const baseRailLf = selectedRailing?.cost ?? 0;

  const referencesDecking =
    rowCat.includes("deck") ||
    rowCat === "decking_options" ||
    rowCat === "bench" ||
    pickedNameLc.includes("deck") ||
    pickedNameLc.includes("stair landing") ||
    pickedNameLc.includes("diagonal") ||
    pickedNameLc.includes("picture frame") ||
    pickedNameLc.includes("pic frame") ||
    pickedNameLc.includes("planter") ||
    pickedNameLc.includes("ramp") ||
    pickedNameLc.includes("board over board");

  const referencesRailing =
    rowCat.includes("rail") ||
    pickedNameLc.includes("rail") ||
    pickedNameLc.includes("baluster");

  // 5) PIC FRAME BORDER
  const isPicFrameBorder =
    pickedName.includes("pic frame") ||
    pickedName.includes("picture frame") ||
    pickedName.includes("picframe");

  if (isPicFrameBorder) {
    if (!selectedDecking || baseDeckSf <= 0) {
      displayUnitCost = 0;
      lineBase = 0;
      tooltip = "Select Decking Type to price picture frame";
    } else {
      displayUnitCost = baseDeckSf * 0.75;
      lineBase = displayUnitCost * qty;
      tooltip = `Pic Frame Border: $${displayUnitCost.toFixed(
        2
      )}/lf (0.75 × $${baseDeckSf.toFixed(2)}/sf)`;
    }
  }
  // PLANTER
  if (isPlanter) {
    if (!selectedDecking || baseDeckSf <= 0) {
      displayUnitCost = 0;
      lineBase = 0;
      tooltip = "Select Decking Type to price planter";
    } else {
      displayUnitCost = baseDeckSf * 0.9;
      lineBase = displayUnitCost * qty;
      tooltip = `Planter: $${displayUnitCost.toFixed(
        2
      )}/lf (0.90 × $${baseDeckSf.toFixed(2)}/sf)`;
    }
  }
  // RAMP
  if (isRamp) {
    if (!selectedDecking || baseDeckSf <= 0) {
      displayUnitCost = 0;
      lineBase = 0;
      tooltip = "Select Decking Type to price ramp";
    } else {
      const mult = Number((picked as any).cost || 0);
      displayUnitCost = baseDeckSf * mult;
      lineBase = displayUnitCost * qty;
      tooltip = `Ramp: $${displayUnitCost.toFixed(2)}/sf (${mult.toFixed(
        2
      )} × $${baseDeckSf.toFixed(2)}/sf)`;
    }
  }

  if (
    pickedUnit === "multiplier" &&
    rowCat !== "bench" &&
    rowCat !== "stair_options" &&
    !isPicFrameBorder &&
    !isPlanter &&
    !isRamp
  ) {
    const base =
      referencesRailing && baseRailLf > 0
        ? baseRailLf
        : referencesDecking && baseDeckSf > 0
        ? baseDeckSf
        : 0;

    if (base <= 0) {
      displayUnitCost = 0;
      lineBase = 0;
      tooltip = referencesRailing
        ? "Select Railing Type to price this item"
        : "Select Decking Type to price this item";
    } else {
      const mult = Number((picked as any).cost || 0);
      displayUnitCost = base * mult;
      lineBase = displayUnitCost * qty;
      tooltip = `multiplier ${mult.toFixed(2)} × base $${base.toFixed(
        2
      )} = $${displayUnitCost.toFixed(2)} per unit`;
    }
  }

  // 8) addon_lf / addon_sf items
  if (
    (pickedUnit === "addon_lf" || pickedUnit === "addon_sf") &&
    rowCat !== "bench"
  ) {
    const base =
      referencesRailing && baseRailLf > 0
        ? baseRailLf
        : referencesDecking && baseDeckSf > 0
        ? baseDeckSf
        : 0;

    if (base <= 0) {
      displayUnitCost = 0;
      lineBase = 0;
      tooltip = referencesRailing
        ? "Select Railing Type to price this item"
        : "Select Decking Type to price this item";
    } else {
      const add = Number((picked as any).cost || 0);
      displayUnitCost = base + add;
      lineBase = displayUnitCost * qty;
      tooltip = `base $${base.toFixed(2)} + add $${add.toFixed(
        2
      )} = $${displayUnitCost.toFixed(2)}`;
    }
  }

  // DEFAULT pricing fallback
  if (displayUnitCost === 0) {
    const fallback = Number((picked as any)?.cost ?? 0);
    displayUnitCost = Number.isFinite(fallback) ? fallback : 0;
    lineBase = displayUnitCost * qty;

    if (!tooltip) {
      tooltip = `unit $${displayUnitCost.toFixed(
        2
      )} × qty ${qty} = $${lineBase.toFixed(2)}`;
    }
  }

  return {
    ...row,
    qty,
    picked: { ...(picked as any), name: pickedNameRaw } as any,
    unitCost: displayUnitCost,
    lineBase,
    unitLabel: (picked as any)?.unit || "ea",
    unit: (picked as any)?.unit || "ea",
    tooltip,
    isFixedPrice: false,
    displayName: pickedNameRaw,
  };
}

/* ============================================================
   computeEstimate — single source of truth for the estimate
   numbers. Both desktop and mobile call this.
   ============================================================ */

export type EstimateInput = {
  pricingItems: PricingItemRow[];
  constructionType: string;
  selectedDeckingId: string;
  selectedRailingId: string;
  selectedStairsId: string;
  selectedFastenerId: string;
  selectedDemoId: string;
  selectedSkirtingId: string;
  skirtingCategory: string;
  deckingSqFt: number;
  railingLf: number;
  stairsCount: number;
  demoQty: number;
  skirtingSf: number;
  addItems: AddItemRow[];
  miValue: number;
  msrpMode: boolean;
  includePermit: boolean;
};

export type EstimateOutput = {
  // option arrays
  deckingOptions: PricingItemRow[];
  railingOptions: PricingItemRow[];
  stairsOptions: PricingItemRow[];
  fastenerOptions: PricingItemRow[];
  demoOptions: PricingItemRow[];
  skirtingOnlyOptions: PricingItemRow[];
  latticeOnlyOptions: PricingItemRow[];
  skirtingLatticeOptions: PricingItemRow[];
  constructionOptions: PricingItemRow[];

  // selected rows
  selectedDecking: PricingItemRow | undefined;
  selectedRailing: PricingItemRow | undefined;
  selectedFastener: PricingItemRow | undefined;
  selectedDemo: PricingItemRow | undefined;
  selectedSkirting: PricingItemRow | undefined;
  selectedConstruction: PricingItemRow | undefined;
  selectedStairsRow: PricingItemRow | undefined;
  selectedStairOption: PricingItemRow | undefined;

  // per-category numbers
  constructionAdj: number;
  baseDeckingUnit: number;
  adjustedDeckingUnit: number;
  deckingSubtotal: number;
  baseRailingUnit: number;
  railingSubtotal: number;
  stairsCalc: { baseUnit: number; effectiveRate: number; tooltip: string };
  baseStairsUnit: number;
  effectiveStairsRate: number;
  stairsSubtotal: number;
  baseFastenerUnit: number;
  fastenerQtyAuto: number;
  fastenerSubtotal: number;
  baseDemoUnit: number;
  demoSubtotal: number;
  skirtingCalc: { effectiveRate: number; tooltip: string };
  effectiveSkirtingRate: number;
  skirtingSubtotal: number;

  // add items
  addItemsDetailed: DetailedAddItem[];
  addItemsSubtotalUpliftable: number;
  addItemsSubtotalFixed: number;
  optionalAddItemsSubtotal: number;

  // uplift / final
  baseProjectTotal: number;
  displayedBasePriceForThresholds: number;
  financeRow: PricingItemRow | undefined;
  perceivedRow: PricingItemRow | undefined;
  financeMultiplier: number;
  perceivedMultiplier: number;
  permitTier: { multiplier: number; threshold: number | null };
  permitMultiplier: number;
  permitThreshold: number | null;
  smallTier: { multiplier: number; threshold: number | null };
  smallProjectMultiplier: number;
  rawFinancePercent: number;
  rawPerceivedPercent: number;
  financePercent: number;
  perceivedPercent: number;
  miPercent: number;
  smallJobPercent: number;
  permitPercent: number;
  totalUpliftPercent: number;
  upliftMultiplier: number;
  totalUpliftDollars: number;
  financeUpliftDollars: number;
  perceivedUpliftDollars: number;
  miUpliftDollars: number;
  permitUpliftDollars: number;
  smallJobUpliftDollars: number;
  projectTotalWithUplift: number;
  finalEstimate: number;
};

export function computeEstimate(input: EstimateInput): EstimateOutput {
  const {
    pricingItems,
    constructionType,
    selectedDeckingId,
    selectedRailingId,
    selectedStairsId,
    selectedFastenerId,
    selectedDemoId,
    selectedSkirtingId,
    skirtingCategory,
    deckingSqFt,
    railingLf,
    stairsCount,
    demoQty,
    skirtingSf,
    addItems,
    miValue,
    msrpMode,
    includePermit,
  } = input;

  // ----- options
  const deckingOptions = filterDeckingOptions(pricingItems);
  const stairsOptions = filterStairsOptions(pricingItems);
  const fastenerOptions = filterFastenerOptions(pricingItems);
  const demoOptions = filterDemoOptions(pricingItems);
  const skirtingOnlyOptions = filterSkirtingOnlyOptions(pricingItems);
  const latticeOnlyOptions = filterLatticeOnlyOptions(pricingItems);
  const skirtingLatticeOptions = filterSkirtingLatticeOptions(
    pricingItems,
    skirtingCategory
  );
  const railingOptions = filterRailingOptions(pricingItems);
  const constructionOptions = filterConstructionOptions(pricingItems);

  // ----- selected rows
  const selectedDecking = deckingOptions.find(
    (d) => String(d.id) === selectedDeckingId
  );
  const selectedRailing = railingOptions.find(
    (r) => String(r.id) === selectedRailingId
  );
  const selectedFastener = fastenerOptions.find(
    (f) => String(f.id) === selectedFastenerId
  );
  const selectedDemo = demoOptions.find(
    (d) => String(d.id) === selectedDemoId
  );
  const selectedSkirting = skirtingLatticeOptions.find(
    (s) => String(s.id) === selectedSkirtingId
  );

  const selectedConstruction = constructionOptions.find((c) => {
    const cn = normalizeName(c.name || "");
    const ct = normalizeName(constructionType || "");
    return (
      cn === ct ||
      (ct && cn.includes(ct)) ||
      (cn && ct.includes(cn)) ||
      cn.startsWith(ct) ||
      ct.startsWith(cn)
    );
  });

  // ----- decking
  const constructionAdj = selectedConstruction?.cost ?? 0;
  const baseDeckingUnit = selectedDecking?.cost ?? 0;
  const adjustedDeckingUnit = baseDeckingUnit + constructionAdj;
  const deckingSubtotal = adjustedDeckingUnit * deckingSqFt;

  // ----- railing
  const baseRailingUnit = selectedRailing?.cost ?? 0;
  const railingSubtotal = baseRailingUnit * railingLf;

  // ----- stairs
  const selectedStairOption = stairsOptions.find(
    (s) => String(s.id) === String(selectedStairsId)
  );
  const stairsCalc = computeEffectiveStairsRate({
    pricingItems,
    selectedDecking,
    stairOptionRow: selectedStairOption || null,
  });
  const baseStairsUnit = stairsCalc.baseUnit;
  const effectiveStairsRate = stairsCalc.effectiveRate;
  const stairsSubtotal = effectiveStairsRate * (stairsCount ?? 0);

  // ----- fasteners
  const baseFastenerUnit = selectedFastener?.cost ?? 0;
  const fastenerQtyAuto = deckingSqFt || 0;
  const fastenerSubtotal = baseFastenerUnit * fastenerQtyAuto;

  // ----- demo
  const baseDemoUnit = selectedDemo?.cost ?? 0;
  const demoSubtotal = baseDemoUnit * demoQty;

  // ----- skirting
  const skirtingCalc = computeEffectiveSkirtingRate({
    selectedDeckingUnit: adjustedDeckingUnit,
    skirtingRow: selectedSkirting,
  });
  const effectiveSkirtingRate = skirtingCalc.effectiveRate;
  const skirtingSubtotal = effectiveSkirtingRate * skirtingSf;

  // ----- add items
  const selectedStairsRow = pricingItems.find(
    (p) => String(p.id) === String(selectedStairsId)
  );

  const addItemsDetailed: DetailedAddItem[] = addItems.map((row) =>
    priceAddItem(row, {
      pricingItems,
      selectedDecking,
      selectedRailing,
      selectedStairsRow,
    })
  );

  // Optional items price out but are EXCLUDED from the main total. They're
  // surfaced separately on the proposal under "Optional Add-Ons".
  const addItemsSubtotalUpliftable = addItemsDetailed.reduce(
    (sum, r) => sum + (!r.isFixedPrice && !r.optional ? r.lineBase || 0 : 0),
    0
  );
  const addItemsSubtotalFixed = addItemsDetailed.reduce(
    (sum, r) => sum + (r.isFixedPrice && !r.optional ? r.lineBase || 0 : 0),
    0
  );
  const optionalAddItemsUpliftableBase = addItemsDetailed.reduce(
    (sum, r) => sum + (!r.isFixedPrice && r.optional ? r.lineBase || 0 : 0),
    0
  );
  const optionalAddItemsFixedBase = addItemsDetailed.reduce(
    (sum, r) => sum + (r.isFixedPrice && r.optional ? r.lineBase || 0 : 0),
    0
  );

  // ----- uplift / final estimate
  const baseProjectTotal =
    deckingSubtotal +
    railingSubtotal +
    stairsSubtotal +
    fastenerSubtotal +
    demoSubtotal +
    skirtingSubtotal +
    addItemsSubtotalUpliftable;

  const displayedBasePriceForThresholds =
    baseProjectTotal + addItemsSubtotalFixed;

  const financeRow = pricingItems.find(
    (row) => row.unit === "global_multiplier" && row.name === "Finance"
  );
  const perceivedRow = pricingItems.find(
    (row) => row.unit === "global_multiplier" && row.name === "Perceived Value"
  );

  const financeMultiplier = financeRow?.cost ?? 1;
  const perceivedMultiplier = perceivedRow?.cost ?? 1;

  const permitTier = includePermit
    ? getPermitTierForTotal(pricingItems, baseProjectTotal)
    : { multiplier: 1, threshold: null };
  const permitMultiplier = permitTier.multiplier;
  const permitThreshold = permitTier.threshold;

  const smallTier = getSmallJobTierForTotal(
    pricingItems,
    displayedBasePriceForThresholds
  ) ?? { multiplier: 1, threshold: null };
  const smallProjectMultiplier =
    baseProjectTotal > 0 ? smallTier.multiplier : 1;

  const rawFinancePercent = (financeMultiplier - 1) * 100;
  const rawPerceivedPercent = (perceivedMultiplier - 1) * 100;

  const financePercent = msrpMode ? 0 : rawFinancePercent;
  const perceivedPercent = msrpMode ? 0 : rawPerceivedPercent;

  const miPercent = miValue || 0;

  const smallJobPercent = (smallProjectMultiplier - 1) * 100;
  const permitPercent = (permitMultiplier - 1) * 100;

  const totalUpliftPercent =
    financePercent +
    perceivedPercent +
    miPercent +
    smallJobPercent +
    permitPercent;

  const upliftMultiplier = 1 + totalUpliftPercent / 100;

  const totalUpliftDollars =
    baseProjectTotal > 0 ? (baseProjectTotal * totalUpliftPercent) / 100 : 0;
  const financeUpliftDollars =
    baseProjectTotal > 0 ? (baseProjectTotal * financePercent) / 100 : 0;
  const perceivedUpliftDollars =
    baseProjectTotal > 0 ? (baseProjectTotal * perceivedPercent) / 100 : 0;
  const miUpliftDollars =
    baseProjectTotal > 0 ? (baseProjectTotal * miPercent) / 100 : 0;
  const permitUpliftDollars =
    baseProjectTotal > 0 ? (baseProjectTotal * permitPercent) / 100 : 0;
  const smallJobUpliftDollars =
    baseProjectTotal > 0 ? (baseProjectTotal * smallJobPercent) / 100 : 0;

  const projectTotalWithUplift =
    baseProjectTotal > 0 ? baseProjectTotal * upliftMultiplier : 0;
  const finalEstimate = projectTotalWithUplift + addItemsSubtotalFixed;

  // Same display basis as the right-rail breakdown: fixed items shown raw,
  // upliftable items shown at lineBase × upliftMultiplier — so the customer-
  // facing optional total matches what they'd pay to add the item.
  const optionalAddItemsSubtotal =
    optionalAddItemsFixedBase +
    optionalAddItemsUpliftableBase * upliftMultiplier;

  return {
    deckingOptions,
    railingOptions,
    stairsOptions,
    fastenerOptions,
    demoOptions,
    skirtingOnlyOptions,
    latticeOnlyOptions,
    skirtingLatticeOptions,
    constructionOptions,

    selectedDecking,
    selectedRailing,
    selectedFastener,
    selectedDemo,
    selectedSkirting,
    selectedConstruction,
    selectedStairsRow,
    selectedStairOption,

    constructionAdj,
    baseDeckingUnit,
    adjustedDeckingUnit,
    deckingSubtotal,
    baseRailingUnit,
    railingSubtotal,
    stairsCalc,
    baseStairsUnit,
    effectiveStairsRate,
    stairsSubtotal,
    baseFastenerUnit,
    fastenerQtyAuto,
    fastenerSubtotal,
    baseDemoUnit,
    demoSubtotal,
    skirtingCalc,
    effectiveSkirtingRate,
    skirtingSubtotal,

    addItemsDetailed,
    addItemsSubtotalUpliftable,
    addItemsSubtotalFixed,
    optionalAddItemsSubtotal,

    baseProjectTotal,
    displayedBasePriceForThresholds,
    financeRow,
    perceivedRow,
    financeMultiplier,
    perceivedMultiplier,
    permitTier,
    permitMultiplier,
    permitThreshold,
    smallTier,
    smallProjectMultiplier,
    rawFinancePercent,
    rawPerceivedPercent,
    financePercent,
    perceivedPercent,
    miPercent,
    smallJobPercent,
    permitPercent,
    totalUpliftPercent,
    upliftMultiplier,
    totalUpliftDollars,
    financeUpliftDollars,
    perceivedUpliftDollars,
    miUpliftDollars,
    permitUpliftDollars,
    smallJobUpliftDollars,
    projectTotalWithUplift,
    finalEstimate,
  };
}
